import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Pencil,
  X,
  ArrowUpDown,
  Filter,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
  ScanSearch,
  ExternalLink,
  ChevronsLeft,
  ChevronsRight,
  Settings2,
  GripVertical,
  RotateCcw,
  Search,
  BookmarkPlus,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, money, dateFmt } from "../lib/api.js";
import { Card, Pill, Empty, Modal } from "../components/UI.jsx";
import MovementModal from "../components/MovementModal.jsx";
import PeriodFilter, { resolvePeriod } from "../components/PeriodFilter.jsx";

const tone = (t) =>
  t === "passive_income"
    ? "green"
    : t === "expense"
      ? "red"
      : t === "investment"
        ? "blue"
        : "";
const nextStatus = (s) =>
  s === "not_cleared"
    ? "cleared"
    : s === "cleared"
      ? "reconciled"
      : "not_cleared";
const sortVal = (x, k) =>
  k === "amount"
    ? Number(x.amount)
    : k === "date"
      ? String(x.operation_date)
      : k === "description"
        ? String(x.description || "").toLowerCase()
        : k === "account"
          ? String(x.account_name || "").toLowerCase()
          : k === "category"
            ? String(x.category_name || "").toLowerCase()
            : k === "bucket"
              ? String(x.budget_bucket || "")
              : k === "status"
                ? String(x.clearance_status || "")
                : String(x.financial_type || "");
const DEFAULT_COLUMNS = [
  { key: "date", label: "Date", width: 115, visible: true },
  { key: "account", label: "Account", width: 150, visible: true },
  { key: "description", label: "Description", width: 330, visible: true },
  { key: "category", label: "Category", width: 180, visible: true },
  { key: "type", label: "Type", width: 135, visible: true },
  { key: "bucket", label: "50/30/20", width: 115, visible: true },
  { key: "status", label: "Status", width: 125, visible: true },
  { key: "amount", label: "Amount", width: 130, visible: true },
  { key: "actions", label: "Actions", width: 92, visible: true },
];
const readColumnPrefs = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (parsed?.columns?.length) return parsed;
  } catch {}
  return { columns: DEFAULT_COLUMNS, density: "normal" };
};
const EMPTY_FILTERS = {
  categories: [],
  types: [],
  buckets: [],
  statuses: [],
  costCenters: [],
  projects: [],
  assets: [],
  tags: [],
  currencies: [],
  direction: "all",
  amountMin: "",
  amountMax: "",
  text: "",
  reviewOnly: false,
};

export default function Transactions({ onAddMovement }) {
  const [params] = useSearchParams(),
    fixedAccountId = params.get("accountId") || "",
    incomeSourceFilter = params.get("incomeSource") || "";
  const navigate = useNavigate();
  const [t, setT] = useState([]),
    [cfg, setCfg] = useState(null),
    [modal, setModal] = useState(false),
    [editing, setEditing] = useState(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState(null),
    [showFilters, setShowFilters] = useState(false),
    [period, setPeriod] = useState(() => ({
      mode: "all",
      ...resolvePeriod("all"),
      years: [],
      months: [],
    })),
    [sort, setSort] = useState({ key: "date", dir: "desc" }),
    [bulk, setBulk] = useState(false),
    [selected, setSelected] = useState(new Set()),
    [bulkField, setBulkField] = useState("status"),
    [bulkValue, setBulkValue] = useState("cleared"),
    [page, setPage] = useState(1),
    [review, setReview] = useState(() =>
      params.get("reviewIncome") === "1" ? "setup" : null,
    ),
    [reviewIndex, setReviewIndex] = useState(0),
    [reviewSetup, setReviewSetup] = useState({
      field:
        params.get("reviewIncome") === "1"
          ? "incomeQuadrantOverride"
          : "budgetBucket",
      scope: "missing",
    }),
    [accountFilters, setAccountFilters] = useState(new Set()),
    [advanced, setAdvanced] = useState(EMPTY_FILTERS),
    [columnModal, setColumnModal] = useState(false),
    [columns, setColumns] = useState(DEFAULT_COLUMNS),
    [density, setDensity] = useState("normal"),
    [dragColumn, setDragColumn] = useState(null),
    [categorySearch, setCategorySearch] = useState(""),
    [categoryPath, setCategoryPath] = useState([]),
    [reviewCategoryIds, setReviewCategoryIds] = useState([]),
    [accountPicker, setAccountPicker] = useState(""),
    [savedViews, setSavedViews] = useState([]);

  const load = () =>
    Promise.all([
      api(
        "/transactions" +
          (fixedAccountId
            ? `?accountId=${fixedAccountId}&limit=5000`
            : "?limit=5000"),
      ),
      api("/config"),
      api("/saved-filter-views?page=movements").catch(() => []),
    ])
      .then(([rows, c, views]) => {
        setT(rows);
        setCfg(c);
        const prefs = readColumnPrefs(c?.preferences?.movement_columns_json);
        setColumns(prefs.columns);
        setDensity(prefs.density || "normal");
        setSavedViews(views);
        if (!fixedAccountId) {
          const ids = [...new Set(rows.map((x) => Number(x.account_id)))];
          setAccountFilters((prev) => {
            if (!prev.size) return new Set(ids);
            const kept = [...prev].filter((x) => ids.includes(Number(x)));
            return new Set(kept.length ? kept : ids);
          });
        }
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("pisto:data-changed", h);
    return () => window.removeEventListener("pisto:data-changed", h);
  }, [fixedAccountId]);
  useEffect(
    () => setPage(1),
    [filter, period, sort.key, sort.dir, accountFilters, advanced.direction],
  );

  const del = async (x) => {
    const linked = Boolean(x.transfer_group_id);
    if (
      confirm(
        linked
          ? "Delete both sides of this linked transfer?"
          : "Delete this movement?",
      )
    ) {
      try {
        await api(
          linked
            ? `/transfers/${x.transfer_group_id}?accountId=${x.account_id}`
            : "/transactions/" + x.id + "?accountId=" + x.account_id,
          { method: "DELETE" },
        );
        load();
      } catch (e) {
        setError(e.message);
      }
    }
  };
  const toggleStatus = async (x) => {
    try {
      await api(`/transactions/${x.id}/status`, {
        method: "PATCH",
        body: {
          accountId: x.account_id,
          status: nextStatus(x.clearance_status || "not_cleared"),
        },
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const accountChoices = useMemo(() => {
    const m = new Map();
    for (const account of cfg?.accounts || []) {
      const location = (cfg?.locations || []).find((x) => String(x.id) === String(account.location_id));
      const costCenter = (cfg?.costCenters || []).find((x) => String(x.id) === String(account.cost_center_id));
      m.set(Number(account.id), { id:Number(account.id), name:account.name, currency:account.currency, locationName:location?.name||"No place", costCenterId:account.cost_center_id, costCenterName:costCenter?.name||"Unassigned" });
    }
    for (const x of t) if (!m.has(Number(x.account_id))) m.set(Number(x.account_id), { id:Number(x.account_id), name:x.account_name||`Account ${x.account_id}`, currency:x.currency, locationName:"No place", costCenterId:x.cost_center_id, costCenterName:x.cost_center_name||"Unassigned" });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [t, cfg]);
  const accountGroups = useMemo(() => Object.entries(accountChoices.reduce((out, account) => {
    (out[account.locationName] ||= []).push(account); return out;
  }, {})).sort(([a],[b]) => a.localeCompare(b)), [accountChoices]);
  const toggleCostCenterQuick = (id) => {
    const value = String(id), next = advanced.costCenters.includes(value)
      ? advanced.costCenters.filter((x) => x !== value)
      : [...advanced.costCenters, value];
    setAdvanced({ ...advanced, costCenters: next });
  };
  const saveCurrentView = async () => {
    const name = prompt("Name this movement view");
    if (!name?.trim()) return;
    await api("/saved-filter-views", { method:"POST", body:{ page:"movements", name:name.trim(), filters:{ accountIds:[...accountFilters], advanced, period } } });
    setSavedViews(await api("/saved-filter-views?page=movements"));
  };
  const applySavedView = (id) => {
    const view = savedViews.find((x) => String(x.id) === String(id));
    if (!view) return;
    setAccountFilters(new Set((view.filters?.accountIds || []).map(Number)));
    setAdvanced({ ...EMPTY_FILTERS, ...(view.filters?.advanced || {}) });
    if (view.filters?.period) setPeriod(view.filters.period);
  };

  const filtered = useMemo(
    () =>
      t.filter(
        (x) =>
          (fixedAccountId || accountFilters.has(Number(x.account_id))) &&
          (!incomeSourceFilter || x.income_source === incomeSourceFilter) &&
          (!filter ||
            (filter.type === "category"
              ? String(x.category_id) === String(filter.value) ||
                (x.additional_categories || []).some(
                  (c) => String(c.id) === String(filter.value),
                )
              : filter.type === "tag"
                ? (x.tags || []).includes(filter.value)
                : filter.type === "project"
                  ? String(x.project_id) === String(filter.value)
                  : filter.type === "costCenter"
                    ? String(x.cost_center_id) === String(filter.value)
                    : filter.type === "type"
                      ? x.financial_type === filter.value
                      : filter.type === "bucket"
                        ? (x.budget_bucket || "unclassified") === filter.value
                        : filter.type === "status"
                          ? (x.clearance_status || "not_cleared") ===
                            filter.value
                          : true)) &&
          (!advanced.categories.length ||
            advanced.categories.includes(String(x.category_id)) ||
            (x.additional_categories || []).some((c) =>
              advanced.categories.includes(String(c.id)),
            )) &&
          (!advanced.types.length ||
            advanced.types.includes(String(x.financial_type || ""))) &&
          (!advanced.buckets.length ||
            advanced.buckets.includes(
              String(x.budget_bucket || "unclassified"),
            )) &&
          (!advanced.statuses.length ||
            advanced.statuses.includes(
              String(x.clearance_status || "not_cleared"),
            )) &&
          (!advanced.costCenters.length ||
            advanced.costCenters.includes(String(x.cost_center_id || ""))) &&
          (!advanced.projects.length ||
            advanced.projects.includes(String(x.project_id || ""))) &&
          (!advanced.assets.length ||
            advanced.assets.includes(String(x.asset_id || ""))) &&
          (!advanced.tags.length ||
            (x.tags || []).some((tag) => advanced.tags.includes(tag))) &&
          (!advanced.currencies.length ||
            advanced.currencies.includes(String(x.currency || ""))) &&
          (advanced.direction === "all" ||
            (advanced.direction === "income"
              ? Number(x.amount) > 0
              : Number(x.amount) < 0)) &&
          (advanced.amountMin === "" ||
            Math.abs(Number(x.amount)) >= Number(advanced.amountMin)) &&
          (advanced.amountMax === "" ||
            Math.abs(Number(x.amount)) <= Number(advanced.amountMax)) &&
          (!advanced.text ||
            [x.description, x.counterparty, x.reference].some((v) =>
              String(v || "")
                .toLowerCase()
                .includes(advanced.text.toLowerCase()),
            )) &&
          (!advanced.reviewOnly ||
            Boolean(x.review_reason) ||
            !x.category_id ||
            (Number(x.amount) < 0 &&
              (x.budget_bucket || "unclassified") === "unclassified")) &&
          (!period.from ||
            String(x.operation_date).slice(0, 10) >= period.from) &&
          (!period.to || String(x.operation_date).slice(0, 10) <= period.to) &&
          (period.mode !== "years" ||
            !period.years?.length ||
            period.years.includes(
              Number(String(x.operation_date).slice(0, 4)),
            )) &&
          (period.mode !== "months" ||
            !period.months?.length ||
            period.months.includes(
              Number(String(x.operation_date).slice(5, 7)),
            )),
      ),
    [
      t,
      filter,
      advanced,
      period,
      fixedAccountId,
      accountFilters,
      incomeSourceFilter,
    ],
  );

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const av = sortVal(a, sort.key),
          bv = sortVal(b, sort.key);
        return (av > bv ? 1 : av < bv ? -1 : 0) * (sort.dir === "asc" ? 1 : -1);
      }),
    [filtered, sort],
  );
  const pageSize = 100,
    pages = Math.max(1, Math.ceil(sorted.length / pageSize)),
    visible = sorted.slice((page - 1) * pageSize, page * pageSize);

  const openAdd = () =>
    onAddMovement
      ? onAddMovement(
          fixedAccountId
            ? { accountId: Number(fixedAccountId), source: "movements-filter" }
            : {},
        )
      : (setEditing(null), setModal(true));
  const sortHead = (key, label) => (
    <button
      className="sort-head"
      title={`Sort by ${label}`}
      onClick={() =>
        setSort((s) => ({
          key,
          dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
        }))
      }
    >
      {label}
      <ArrowUpDown size={13} />
    </button>
  );

  const toggleAccount = (id) =>
    setAccountFilters((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const selectedValues = (e) =>
    [...e.target.selectedOptions].map((o) => o.value);
  const allTags = useMemo(
    () => [...new Set(t.flatMap((x) => x.tags || []))].sort(),
    [t],
  );
  const currencies = useMemo(
    () => [...new Set(t.map((x) => x.currency).filter(Boolean))].sort(),
    [t],
  );
  const allAccountsSelected =
    accountChoices.length > 0 && accountFilters.size === accountChoices.length;

  const toggleSelect = (x) =>
    setSelected((s) => {
      const n = new Set(s),
        k = `${x.account_id}:${x.id}`;
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((x) => selected.has(`${x.account_id}:${x.id}`));
  const selectVisible = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected)
        visible.forEach((x) => n.delete(`${x.account_id}:${x.id}`));
      else visible.forEach((x) => n.add(`${x.account_id}:${x.id}`));
      return n;
    });
  const selectedItems = () =>
    t
      .filter((x) => selected.has(`${x.account_id}:${x.id}`))
      .map((x) => ({ id: x.id, accountId: x.account_id }));

  const applyBulk = async (action = "update") => {
    const items = selectedItems();
    if (!items.length) return;
    try {
      if (
        action === "delete" &&
        !confirm(`Delete ${items.length} selected movements?`)
      )
        return;
      await api("/transactions/bulk", {
        method: "POST",
        body: { items, action, field: bulkField, value: bulkValue },
      });
      setSelected(new Set());
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const bulkOptions = () =>
    bulkField === "category"
      ? (cfg?.categories || []).map((x) => (
          <option key={x.id} value={x.id}>
            {x.custom_name}
          </option>
        ))
      : bulkField === "costCenter"
        ? (cfg?.costCenters || []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))
        : bulkField === "project"
          ? (cfg?.projects || []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))
          : bulkField === "financialType"
            ? [
                "active_income",
                "passive_income",
                "expense",
                "investment",
                "transfer",
                "excluded",
              ].map((x) => <option key={x}>{x}</option>)
            : bulkField === "budgetBucket"
              ? ["needs", "wants", "goals", "unclassified"].map((x) => (
                  <option key={x}>{x}</option>
                ))
              : ["not_cleared", "cleared", "reconciled"].map((x) => (
                  <option key={x}>{x}</option>
                ));

  const beginReview = () => {
    let rows = [...filtered];
    if (reviewSetup.field === "budgetBucket")
      rows = rows.filter(
        (x) =>
          Number(x.amount) < 0 &&
          x.financial_type !== "transfer" &&
          x.financial_type !== "excluded",
      );
    if (reviewSetup.field === "assetRole")
      rows = rows.filter((x) => x.asset_id);
    if (reviewSetup.field === "incomeQuadrantOverride")
      rows = rows.filter((x) => x.is_income_source_candidate);
    if (reviewSetup.scope === "missing")
      rows = rows.filter((x) =>
        reviewSetup.field === "budgetBucket"
          ? (x.budget_bucket || "unclassified") === "unclassified"
          : reviewSetup.field === "category"
            ? !x.category_id
            : reviewSetup.field === "project"
              ? !x.project_id
              : reviewSetup.field === "costCenter"
                ? !x.cost_center_id
                : reviewSetup.field === "status"
                  ? (x.clearance_status || "not_cleared") === "not_cleared"
                  : reviewSetup.field === "assetRole"
                    ? !x.asset_role
                    : reviewSetup.field === "incomeQuadrantOverride"
                      ? x.income_source === "unclassified"
                    : !x.financial_type,
      );
    setReview(rows);
    setReviewIndex(0);
    setCategorySearch("");
    setCategoryPath([]);
    const first = rows[0];
    setReviewCategoryIds(
      first
        ? [
            first.category_id,
            ...(first.additional_categories || []).map((c) => c.id),
          ]
            .filter(Boolean)
            .map(Number)
        : [],
    );
  };
  const advanceReview = () => {
    if (reviewIndex < review.length - 1) {
      const next = review[reviewIndex + 1];
      setReviewIndex((i) => i + 1);
      setCategoryPath([]);
      setReviewCategoryIds(
        [
          next.category_id,
          ...(next.additional_categories || []).map((c) => c.id),
        ]
          .filter(Boolean)
          .map(Number),
      );
      setCategorySearch("");
    }
  };
  const reviewSave = async (value) => {
    const x = review?.[reviewIndex];
    if (!x) return;
    try {
      const requestKey =
        {
          category: "categoryId",
          project: "projectId",
          costCenter: "costCenterId",
          status: "clearanceStatus",
        }[reviewSetup.field] || reviewSetup.field;
      const body = { accountId: x.account_id, [requestKey]: value };
      await api(`/transactions/${x.id}/classify`, { method: "PATCH", body });
      const prop =
        reviewSetup.field === "budgetBucket"
          ? "budget_bucket"
          : reviewSetup.field === "category"
            ? "category_id"
            : reviewSetup.field === "project"
              ? "project_id"
              : reviewSetup.field === "costCenter"
                ? "cost_center_id"
                : reviewSetup.field === "status"
                  ? "clearance_status"
                  : reviewSetup.field === "assetRole"
                    ? "asset_role"
                    : reviewSetup.field === "incomeQuadrantOverride"
                      ? "income_quadrant_override"
                    : "financial_type";
      const derived =
        reviewSetup.field === "incomeQuadrantOverride"
          ? {
              income_source: value === "none" ? "none" : value,
              income_source_suggestion: null,
              income_source_reason: "Movement-level income-source choice",
            }
          : {};
      setT((rows) =>
        rows.map((r) =>
          r.id === x.id && r.account_id === x.account_id
            ? { ...r, [prop]: value, ...derived }
            : r,
        ),
      );
      setReview((rows) =>
        rows.map((r) =>
          r.id === x.id && r.account_id === x.account_id
            ? { ...r, [prop]: value, ...derived }
            : r,
        ),
      );
      setError("");
      advanceReview();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveReviewCategories = async () => {
    const x = review?.[reviewIndex];
    if (!x || !reviewCategoryIds.length) return;
    try {
      const primary = reviewCategoryIds[0],
        extras = reviewCategoryIds.slice(1),
        all = cfg?.categories || [],
        primaryCategory = all.find((c) => Number(c.id) === Number(primary));
      await api(`/transactions/${x.id}/classify`, {
        method: "PATCH",
        body: {
          accountId: x.account_id,
          categoryId: primary,
          additionalCategoryIds: extras,
        },
      });
      const additional = extras
        .map((id) => all.find((c) => Number(c.id) === Number(id)))
        .filter(Boolean)
        .map((c) => ({
          id: c.id,
          name: c.custom_name,
          validDirection: c.valid_direction,
        }));
      const updated = {
        category_id: primary,
        category_name: primaryCategory?.custom_name || "",
        additional_categories: additional,
      };
      setT((rows) =>
        rows.map((r) =>
          r.id === x.id && r.account_id === x.account_id
            ? { ...r, ...updated }
            : r,
        ),
      );
      setReview((rows) =>
        rows.map((r) =>
          r.id === x.id && r.account_id === x.account_id
            ? { ...r, ...updated }
            : r,
        ),
      );
      setError("");
      advanceReview();
    } catch (e) {
      setError(e.message);
    }
  };
  const categoryDirection = (c) => {
    if (c?.valid_direction && c.valid_direction !== "both")
      return c.valid_direction;
    const all = cfg?.categories || [],
      byId = new Map(all.map((x) => [Number(x.id), x]));
    let root = c,
      guard = 0;
    while (root?.parent_id && guard++ < 4)
      root = byId.get(Number(root.parent_id));
    const name = String(root?.custom_name || "")
      .trim()
      .toLowerCase();
    return name === "income"
      ? "income"
      : name === "expense"
        ? "expense"
        : "both";
  };
  const reviewChoices = (current) =>
    reviewSetup.field === "budgetBucket"
      ? [
          ["needs", "Needs"],
          ["wants", "Wants"],
          ["goals", "Goals / savings"],
          ["unclassified", "Unclassified"],
        ]
      : reviewSetup.field === "category"
        ? (cfg?.categories || [])
            .filter(
              (c) =>
                c.is_active !== 0 &&
                [
                  "both",
                  Number(current?.amount) >= 0 ? "income" : "expense",
                ].includes(categoryDirection(c)),
            )
            .map((x) => [x.id, x.custom_name])
        : reviewSetup.field === "project"
          ? (cfg?.projects || []).map((x) => [x.id, x.name])
          : reviewSetup.field === "costCenter"
            ? (cfg?.costCenters || []).map((x) => [x.id, x.name])
            : reviewSetup.field === "status"
              ? [
                  ["not_cleared", "Not cleared"],
                  ["cleared", "Cleared"],
                  ["reconciled", "Reconciled"],
                ]
              : reviewSetup.field === "incomeQuadrantOverride"
                ? [
                    ["employee", "Employee"],
                    ["self_employed", "Self-employed"],
                    ["business_owner", "Business owner"],
                    ["investor", "Investor"],
                    ["none", "None of these / not income"],
                  ]
                    .sort(([a], [b]) =>
                      a === current?.income_source_suggestion
                        ? -1
                        : b === current?.income_source_suggestion
                          ? 1
                          : 0,
                    )
                    .map(([value, label]) => [
                      value,
                      `${label}${value === current?.income_source_suggestion ? " · Suggested" : ""}`,
                    ])
              : reviewSetup.field === "assetRole"
                ? (() => {
                    const type = (cfg?.assets || []).find(
                      (a) => String(a.id) === String(current?.asset_id),
                    )?.asset_type;
                    const values =
                      type === "real_estate"
                        ? [
                            "purchase",
                            "rental income",
                            "rental payment",
                            "renovation",
                            "maintenance",
                            "association/shared-area fees",
                            "property tax",
                            "insurance",
                            "utilities/services",
                            "agent fee",
                            "notary/legal cost",
                            "sale",
                            "other",
                          ]
                        : type === "vehicle"
                          ? [
                              "purchase",
                              "loan payment",
                              "insurance",
                              "registration/tax",
                              "fuel",
                              "maintenance",
                              "repair",
                              "parking/tolls",
                              "rental income",
                              "sale",
                              "other",
                            ]
                          : type === "loan_liability"
                            ? [
                                "loan received",
                                "principal repayment",
                                "interest",
                                "fee",
                                "other",
                              ]
                            : ["stock", "etf", "fund"].includes(type)
                              ? [
                                  "purchase",
                                  "sale",
                                  "dividend",
                                  "tax",
                                  "commission",
                                  "custody fee",
                                  "other",
                                ]
                              : [
                                  "purchase",
                                  "sale",
                                  "income",
                                  "interest",
                                  "maintenance",
                                  "tax",
                                  "commission",
                                  "other",
                                ];
                    return values.map((x) => [x, x]);
                  })()
                : [
                    ["active_income", "Active income"],
                    ["passive_income", "Passive income"],
                    ["expense", "Expense"],
                    ["investment", "Investment"],
                    ["transfer", "Transfer"],
                    ["excluded", "Excluded"],
                  ];
  const displayedColumns = columns.filter(
    (c) => c.visible && (!fixedAccountId || c.key !== "account"),
  );
  const saveColumns = async (next) => {
    const value = next || columns;
    setColumns(value);
    try {
      await api("/preferences/movement-columns", {
        method: "PUT",
        body: { columns: value, density },
      });
    } catch (e) {
      setError(e.message);
    }
  };
  const resetColumns = () => {
    setColumns(DEFAULT_COLUMNS);
    setDensity("normal");
  };
  const dropColumn = (target) => {
    if (!dragColumn || dragColumn === target) return;
    setColumns((cols) => {
      const next = [...cols],
        from = next.findIndex((c) => c.key === dragColumn),
        to = next.findIndex((c) => c.key === target);
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setDragColumn(null);
  };
  const beginResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const start = e.clientX,
      initial = columns.find((c) => c.key === key)?.width || 120;
    const move = (ev) =>
      setColumns((cols) =>
        cols.map((c) =>
          c.key === key
            ? {
                ...c,
                width: Math.max(
                  70,
                  Math.min(700, initial + ev.clientX - start),
                ),
              }
            : c,
        ),
      );
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const openMovement = (x) => {
    setEditing(x);
    setModal(true);
  };
  const renderCell = (x, key) =>
    key === "date" ? (
      dateFmt(x.operation_date)
    ) : key === "account" ? (
      <Pill
        onClick={(e) => {
          e.stopPropagation();
          setAccountFilters(new Set([Number(x.account_id)]));
        }}
      >
        {x.account_name}
      </Pill>
    ) : key === "description" ? (
      <div className="movement-description" title={x.description || ""}>
        <b>{x.description || "—"}</b>
        {x.counterparty && <small className="cell-sub">{x.counterparty}</small>}
        {x.review_reason === "stock_purchase" && (
          <button
            className="stock-review-link"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/investments?reviewMovement=${x.id}&accountId=${x.account_id}`,
              );
            }}
          >
            Review possible stock purchase →
          </button>
        )}
        {(x.tags || []).length > 0 && (
          <div className="table-tags">
            {x.tags.map((tag) => (
              <Pill
                key={tag}
                onClick={(e) => {
                  e.stopPropagation();
                  setFilter({ type: "tag", value: tag, label: tag });
                }}
              >
                {tag}
              </Pill>
            ))}
          </div>
        )}
      </div>
    ) : key === "category" ? (
      <div className="table-tags">
        {x.category_name ? (
          <Pill
            onClick={(e) => {
              e.stopPropagation();
              setFilter({
                type: "category",
                value: x.category_id,
                label: x.category_name,
              });
            }}
          >
            {x.category_name}
          </Pill>
        ) : (
          <span className="muted">—</span>
        )}
        {(x.additional_categories || []).map((c) => (
          <Pill
            key={c.id}
            onClick={(e) => {
              e.stopPropagation();
              setFilter({ type: "category", value: c.id, label: c.name });
            }}
          >
            {c.name}
          </Pill>
        ))}
        {x.project_name && (
          <Pill
            onClick={(e) => {
              e.stopPropagation();
              setFilter({
                type: "project",
                value: x.project_id,
                label: x.project_name,
              });
            }}
          >
            {x.project_name}
          </Pill>
        )}
      </div>
    ) : key === "type" ? (
      <Pill
        tone={tone(x.financial_type)}
        onClick={(e) => {
          e.stopPropagation();
          setFilter({
            type: "type",
            value: x.financial_type,
            label:
              x.asset_role === "purchase"
                ? "Investment purchase"
                : x.asset_role === "sale"
                  ? "Investment sale"
                  : String(x.financial_type || "").replaceAll("_", " "),
          });
        }}
      >
        {x.asset_role === "purchase"
          ? "Investment purchase"
          : x.asset_role === "sale"
            ? "Investment sale"
            : String(x.financial_type || "").replaceAll("_", " ")}
      </Pill>
    ) : key === "bucket" ? (
      Number(x.amount) < 0 && x.financial_type !== "transfer" ? (
        <Pill
          onClick={(e) => {
            e.stopPropagation();
            setFilter({
              type: "bucket",
              value: x.budget_bucket || "unclassified",
              label: (x.budget_bucket || "unclassified").replaceAll("_", " "),
            });
          }}
        >
          {(x.budget_bucket || "unclassified").replaceAll("_", " ")}
        </Pill>
      ) : (
        <span className="muted">—</span>
      )
    ) : key === "status" ? (
      <Pill
        onClick={(e) => {
          e.stopPropagation();
          setFilter({
            type: "status",
            value: x.clearance_status || "not_cleared",
            label: String(x.clearance_status || "not_cleared").replaceAll(
              "_",
              " ",
            ),
          });
        }}
        title="Filter by this status"
      >
        {String(x.clearance_status || "not_cleared").replaceAll("_", " ")}
      </Pill>
    ) : key === "amount" ? (
      money(x.amount, x.currency)
    ) : (
      <div className="row-actions">
        <button
          className="icon"
          title="Edit movement"
          onClick={(e) => {
            e.stopPropagation();
            openMovement(x);
          }}
        >
          <Pencil size={15} />
        </button>
        <button
          className="icon"
          title="Delete movement"
          onClick={(e) => {
            e.stopPropagation();
            del(x);
          }}
        >
          <Trash2 size={15} />
        </button>
      </div>
    );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>In / out movements</h1>
          <p>
            {fixedAccountId
              ? "Movements for the selected account."
              : "Normalized movements across your accounts."}
          </p>
        </div>
        <div className="actions">
          <button
            className="secondary"
            title="Show date and table filters"
            onClick={() => setShowFilters((x) => !x)}
          >
            <Filter size={16} /> Filters
          </button>
          <button
            className="secondary"
            title="Choose, resize and reorder table columns"
            onClick={() => setColumnModal(true)}
          >
            <Settings2 size={16} /> Configure columns
          </button>
          <button
            className="secondary"
            title="Select many movements and update them together"
            onClick={() => {
              setBulk((x) => !x);
              setSelected(new Set());
            }}
          >
            {bulk ? <X size={16} /> : <CheckSquare size={16} />}{" "}
            {bulk ? "Exit bulk edit" : "Bulk edit"}
          </button>
          <button
            className="secondary"
            title="Review one classification field movement by movement"
            onClick={() => setReview("setup")}
          >
            <ScanSearch size={16} /> Quick review
          </button>
          <button className="primary" onClick={openAdd}>
            <Plus size={17} /> Add movement
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}

      <Card className="account-filter-card movement-filter-card">
        <div className="movement-filter-header">
          <div className="filter-label">
            <b>{!fixedAccountId && accountChoices.length > 1 ? "Accounts" : "Movements"}</b>
            <small>
              {!fixedAccountId && accountChoices.length > 1
                ? "Multi-select the accounts shown in the table."
                : "Choose money in, money out or all movements."}
            </small>
          </div>
          <div className="direction-filter-pills" aria-label="Movement direction">
            {[
              ["all", "All"],
              ["income", "In"],
              ["expense", "Out"],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={
                  advanced.direction === value
                    ? "filter-pill active"
                    : "filter-pill"
                }
                onClick={() => setAdvanced({ ...advanced, direction: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {!fixedAccountId && accountChoices.length > 1 && (
          <div className="account-filter-picker">
            <div className="filter-picker-row">
              <label>
                Add accounts by place
                <select value={accountPicker} onChange={(event) => {
                  const value = Number(event.target.value);
                  if (value) setAccountFilters(new Set([...accountFilters, value]));
                  setAccountPicker("");
                }}>
                  <option value="">Choose a place and account…</option>
                  {accountGroups.map(([place, accounts]) => <optgroup key={place} label={place}>{accounts.map((account) => <option value={account.id} key={account.id}>{account.name} · {account.currency}</option>)}</optgroup>)}
                </select>
              </label>
              <label>
                Saved view
                <select value="" onChange={(event) => applySavedView(event.target.value)}>
                  <option value="">Choose saved filters…</option>
                  {savedViews.map((view) => <option value={view.id} key={view.id}>{view.name}</option>)}
                </select>
              </label>
              <button type="button" className="secondary compact" onClick={saveCurrentView}><BookmarkPlus size={15}/> Save view</button>
            </div>
          <div className="filter-pills account-filter-pills selected-account-pills">
            <button
              className={
                allAccountsSelected ? "filter-pill active" : "filter-pill"
              }
              onClick={() =>
                setAccountFilters(new Set(accountChoices.map((x) => x.id)))
              }
            >
              Show all
            </button>
            {accountChoices.filter((a) => accountFilters.has(a.id)).map((a) => (
              <button
                title={`Filter movements from ${a.name}`}
                key={a.id}
                className={
                  accountFilters.has(a.id)
                    ? "filter-pill active"
                    : "filter-pill"
                }
                onClick={() => toggleAccount(a.id)}
              >
                {a.name}
                <small>{a.currency}</small>
                <X size={13}/>
              </button>
            ))}
          </div>
          <div className="filter-pills cost-center-quick-pills">
            <span className="muted">Cost centers</span>
            <button className={!advanced.costCenters.length ? "filter-pill active" : "filter-pill"} onClick={() => setAdvanced({ ...advanced, costCenters: [] })}>All</button>
            {(cfg?.costCenters || []).filter((x) => x.is_active !== 0).map((center) => <button key={center.id} className={advanced.costCenters.includes(String(center.id)) ? "filter-pill active" : "filter-pill"} onClick={() => toggleCostCenterQuick(center.id)}>{center.name}</button>)}
          </div>
          </div>
        )}
      </Card>

      {showFilters && (
        <Card className="filter-panel advanced-filter-panel">
          <PeriodFilter
            value={period}
            onChange={setPeriod}
            availableYears={[
              ...new Set(
                t.map((x) => Number(String(x.operation_date).slice(0, 4))),
              ),
            ]
              .filter(Boolean)
              .sort((a, b) => b - a)}
          />
          <div className="filter-grid">
            <label>
              Categories
              <select
                multiple
                value={advanced.categories}
                onChange={(e) =>
                  setAdvanced({ ...advanced, categories: selectedValues(e) })
                }
              >
                {(cfg?.categories || [])
                  .filter((x) => x.is_active !== 0)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {"—".repeat(Math.max(0, Number(x.level) - 1))}{" "}
                      {x.custom_name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Financial types
              <select
                multiple
                value={advanced.types}
                onChange={(e) =>
                  setAdvanced({ ...advanced, types: selectedValues(e) })
                }
              >
                {[
                  "active_income",
                  "passive_income",
                  "expense",
                  "investment",
                  "transfer",
                  "excluded",
                ].map((x) => (
                  <option key={x}>{x.replaceAll("_", " ")}</option>
                ))}
              </select>
            </label>
            <label>
              50 / 30 / 20
              <select
                multiple
                value={advanced.buckets}
                onChange={(e) =>
                  setAdvanced({ ...advanced, buckets: selectedValues(e) })
                }
              >
                {["needs", "wants", "goals", "unclassified"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                multiple
                value={advanced.statuses}
                onChange={(e) =>
                  setAdvanced({ ...advanced, statuses: selectedValues(e) })
                }
              >
                {["not_cleared", "cleared", "reconciled", "planned"].map(
                  (x) => (
                    <option key={x}>{x.replaceAll("_", " ")}</option>
                  ),
                )}
              </select>
            </label>
            <label>
              Cost centers
              <select
                multiple
                value={advanced.costCenters}
                onChange={(e) =>
                  setAdvanced({ ...advanced, costCenters: selectedValues(e) })
                }
              >
                {(cfg?.costCenters || []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Projects
              <select
                multiple
                value={advanced.projects}
                onChange={(e) =>
                  setAdvanced({ ...advanced, projects: selectedValues(e) })
                }
              >
                {(cfg?.projects || []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Assets / liabilities
              <select
                multiple
                value={advanced.assets}
                onChange={(e) =>
                  setAdvanced({ ...advanced, assets: selectedValues(e) })
                }
              >
                {(cfg?.assets || []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Tags
              <select
                multiple
                value={advanced.tags}
                onChange={(e) =>
                  setAdvanced({ ...advanced, tags: selectedValues(e) })
                }
              >
                {allTags.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Currencies
              <select
                multiple
                value={advanced.currencies}
                onChange={(e) =>
                  setAdvanced({ ...advanced, currencies: selectedValues(e) })
                }
              >
                {currencies.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Direction
              <select
                value={advanced.direction}
                onChange={(e) =>
                  setAdvanced({ ...advanced, direction: e.target.value })
                }
              >
                <option value="all">Income and outgoing</option>
                <option value="income">Money in</option>
                <option value="expense">Money out</option>
              </select>
            </label>
            <label>
              Minimum amount
              <input
                type="number"
                min="0"
                step="any"
                value={advanced.amountMin}
                onChange={(e) =>
                  setAdvanced({ ...advanced, amountMin: e.target.value })
                }
              />
            </label>
            <label>
              Maximum amount
              <input
                type="number"
                min="0"
                step="any"
                value={advanced.amountMax}
                onChange={(e) =>
                  setAdvanced({ ...advanced, amountMax: e.target.value })
                }
              />
            </label>
            <label className="filter-text">
              Description, merchant or reference
              <input
                value={advanced.text}
                onChange={(e) =>
                  setAdvanced({ ...advanced, text: e.target.value })
                }
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={advanced.reviewOnly}
                onChange={(e) =>
                  setAdvanced({ ...advanced, reviewOnly: e.target.checked })
                }
              />{" "}
              Needs review only
            </label>
          </div>
          <button
            className="secondary"
            onClick={() => {
              setPeriod({
                mode: "all",
                ...resolvePeriod("all"),
                years: [],
                months: [],
              });
              setFilter(null);
              setAdvanced(EMPTY_FILTERS);
              if (!fixedAccountId)
                setAccountFilters(new Set(accountChoices.map((x) => x.id)));
            }}
          >
            Clear filters
          </button>
          <span>
            {filtered.length} movement{filtered.length === 1 ? "" : "s"}
          </span>
        </Card>
      )}
      {filter && (
        <div className="filter-chip">
          Filtered by {filter.type}: <b>{filter.label || filter.value}</b>
          <button
            className="icon"
            title="Remove this filter"
            onClick={() => setFilter(null)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {bulk && (
        <Card className="bulk-toolbar">
          <button
            className="secondary compact"
            title="Select or unselect all rows on this page"
            onClick={selectVisible}
          >
            {allVisibleSelected ? (
              <CheckSquare size={15} />
            ) : (
              <Square size={15} />
            )}{" "}
            {allVisibleSelected ? "Unselect page" : "Select page"}
          </button>
          <button
            className="secondary compact"
            onClick={() =>
              setSelected(
                new Set(filtered.map((x) => `${x.account_id}:${x.id}`)),
              )
            }
          >
            Select all filtered
          </button>
          <b>{selected.size} selected</b>
          <select
            title="Field to update"
            value={bulkField}
            onChange={(e) => {
              const v = e.target.value;
              setBulkField(v);
              setBulkValue(
                v === "status"
                  ? "cleared"
                  : v === "financialType"
                    ? "expense"
                    : v === "budgetBucket"
                      ? "unclassified"
                      : "",
              );
            }}
          >
            <option value="status">Status</option>
            <option value="category">Category</option>
            <option value="financialType">Financial class</option>
            <option value="budgetBucket">50/30/20</option>
            <option value="costCenter">Cost center</option>
            <option value="project">Project</option>
          </select>
          <select
            title="New value"
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          >
            <option value="">None</option>
            {bulkOptions()}
          </select>
          <button
            className="primary compact"
            onClick={() => applyBulk("update")}
          >
            Apply
          </button>
          <button
            className="danger compact"
            title="Delete all selected movements"
            onClick={() => applyBulk("delete")}
          >
            <Trash2 size={14} /> Delete
          </button>
        </Card>
      )}

      <Card className={`table-card movements-table-card density-${density}`}>
        {!visible.length ? (
          <Empty
            title="No movements found"
            text="Import a statement, add a movement, or change the current filters."
          />
        ) : (
          <div className="table-wrap movements-table-wrap">
            <table className="movements-table">
              <colgroup>
                {bulk && <col style={{ width: 46 }} />}
                {displayedColumns.map((c) => (
                  <col key={c.key} style={{ width: c.width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {bulk && (
                    <th className="select-column">
                      <button
                        className="icon"
                        title="Select this page"
                        onClick={selectVisible}
                      >
                        {allVisibleSelected ? (
                          <CheckSquare size={16} />
                        ) : (
                          <Square size={16} />
                        )}
                      </button>
                    </th>
                  )}
                  {displayedColumns.map((c) => (
                    <th
                      key={c.key}
                      className={
                        c.key === "actions"
                          ? "sticky-actions"
                          : c.key === "amount"
                            ? "right"
                            : ""
                      }
                      draggable={c.key !== "actions"}
                      onDragStart={() => setDragColumn(c.key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropColumn(c.key)}
                    >
                      <div className="movement-column-head">
                        {c.key === "actions"
                          ? c.label
                          : sortHead(c.key, c.label)}
                        {c.key !== "actions" && (
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => beginResize(e, c.key)}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((x) => (
                  <tr
                    className="movement-row"
                    key={`${x.account_id}:${x.id}`}
                    onClick={() => openMovement(x)}
                  >
                    {bulk && (
                      <td className="select-column">
                        <button
                          className="icon"
                          title="Select movement"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelect(x);
                          }}
                        >
                          {selected.has(`${x.account_id}:${x.id}`) ? (
                            <CheckSquare size={16} />
                          ) : (
                            <Square size={16} />
                          )}
                        </button>
                      </td>
                    )}
                    {displayedColumns.map((c) => (
                      <td
                        key={c.key}
                        className={`${c.key === "actions" ? "sticky-actions" : c.key === "amount" ? (Number(x.amount) >= 0 ? "right positive" : "right negative") : ""}`}
                      >
                        {renderCell(x, c.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="pagination">
            <button
              className="icon"
              title="First page"
              disabled={page === 1}
              onClick={() => setPage(1)}
            >
              <ChevronsLeft />
            </button>
            <button
              className="icon"
              title="Previous page"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft />
            </button>
            <span>
              Page {page} of {pages} · {sorted.length} rows
            </span>
            <button
              className="icon"
              title="Next page"
              disabled={page === pages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight />
            </button>
            <button
              className="icon"
              title="Last page"
              disabled={page === pages}
              onClick={() => setPage(pages)}
            >
              <ChevronsRight />
            </button>
          </div>
        )}
      </Card>

      {columnModal && (
        <Modal
          title="Configure movement columns"
          screenId="D0204"
          onClose={() => setColumnModal(false)}
        >
          <div className="column-config">
            <p className="muted">
              Drag rows to change their order. Actions always stays visible on
              the right.
            </p>
            <label>
              Row density
              <select
                value={density}
                onChange={(e) => setDensity(e.target.value)}
              >
                <option value="compact">Compact</option>
                <option value="normal">Normal</option>
                <option value="comfortable">Comfortable</option>
              </select>
            </label>
            <div className="column-config-list">
              {columns.map((c) => (
                <div
                  key={c.key}
                  draggable={c.key !== "actions"}
                  onDragStart={() => setDragColumn(c.key)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropColumn(c.key)}
                >
                  <GripVertical size={17} />
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={c.visible}
                      disabled={c.key === "actions"}
                      onChange={(e) =>
                        setColumns((cols) =>
                          cols.map((x) =>
                            x.key === c.key
                              ? { ...x, visible: e.target.checked }
                              : x,
                          ),
                        )
                      }
                    />
                    {c.label}
                  </label>
                  <span>{Math.round(c.width)} px</span>
                </div>
              ))}
            </div>
            <div className="review-nav">
              <button className="secondary" onClick={resetColumns}>
                <RotateCcw size={15} /> Restore defaults
              </button>
              <button
                className="primary"
                onClick={async () => {
                  await saveColumns();
                  setColumnModal(false);
                }}
              >
                Save columns
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal && (
        <MovementModal
          open
          initial={editing}
          context={{ accountId: editing?.account_id }}
          onClose={() => {
            setModal(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}

      {review === "setup" && (
        <Modal
          title="Quick review movements"
          screenId="D0203"
          onClose={() => setReview(null)}
        >
          <div className="review-setup">
            <p>
              Choose one field. Pisto will show a focused movement card and
              advance as you classify it.
            </p>
            <label>
              Review field
              <select
                value={reviewSetup.field}
                onChange={(e) =>
                  setReviewSetup({ ...reviewSetup, field: e.target.value })
                }
              >
                <option value="budgetBucket">50 / 30 / 20</option>
                <option value="category">Category</option>
                <option value="costCenter">Cost center</option>
                <option value="project">Project</option>
                <option value="assetRole">Asset movement purpose</option>
                <option value="incomeQuadrantOverride">Income source</option>
                <option value="financialType">Financial class</option>
                <option value="status">Status</option>
              </select>
            </label>
            <label>
              Which movements?
              <select
                value={reviewSetup.scope}
                onChange={(e) =>
                  setReviewSetup({ ...reviewSetup, scope: e.target.value })
                }
              >
                <option value="missing">Only missing / pending values</option>
                <option value="all">All movements in current filters</option>
              </select>
            </label>
            <button className="primary modal-submit" onClick={beginReview}>
              Start review
            </button>
          </div>
        </Modal>
      )}

      {Array.isArray(review) && review.length > 0 && (
        <Modal
          title={`Quick review · ${reviewIndex + 1} of ${review.length}`}
          screenId="D0203"
          onClose={() => setReview(null)}
        >
          <div className="review-card">
            <div className="review-movement">
              <span>
                {dateFmt(review[reviewIndex].operation_date)} ·{" "}
                {review[reviewIndex].account_name}
              </span>
              <h2>{review[reviewIndex].description || "No description"}</h2>
              <strong
                className={
                  Number(review[reviewIndex].amount) >= 0
                    ? "positive"
                    : "negative"
                }
              >
                {money(
                  review[reviewIndex].amount,
                  review[reviewIndex].currency,
                )}
              </strong>
              <div className="pills">
                <Pill>{review[reviewIndex].financial_type}</Pill>
                {review[reviewIndex].category_name && (
                  <Pill>{review[reviewIndex].category_name}</Pill>
                )}
              </div>
              {reviewSetup.field === "incomeQuadrantOverride" && (
                <p className="income-review-suggestion">
                  {review[reviewIndex].income_source_suggestion
                    ? `Suggested: ${String(review[reviewIndex].income_source_suggestion).replaceAll("_", " ")} · `
                    : ""}
                  {review[reviewIndex].income_source_reason}
                </p>
              )}
            </div>
            {reviewSetup.field === "category" ? (
              <div className="category-review">
                <label className="category-search">
                  <Search size={16} />
                  <input
                    placeholder="Type to filter categories"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                  />
                </label>
                <p className="muted">
                  The first selected category is primary for reports. Additional
                  compatible categories do not duplicate the movement amount.
                </p>
                <div className="category-groups">
                  {(() => {
                    const allowed = reviewChoices(review[reviewIndex]).map(([id]) => (cfg?.categories || []).find(c => String(c.id) === String(id))).filter(Boolean);
                    const byId = new Map((cfg?.categories || []).map(c => [String(c.id), c]));
                    const breadcrumb = c => { const names=[c.custom_name]; let parent=byId.get(String(c.parent_id)); while(parent){names.unshift(parent.custom_name);parent=byId.get(String(parent.parent_id))} return names.join(" › ") };
                    const select = c => setReviewCategoryIds(ids => ids.includes(Number(c.id)) ? ids.filter(id => id !== Number(c.id)) : [...ids, Number(c.id)]);
                    if(categorySearch.trim()) return <section><b>Matching categories</b><div className="review-choices">{allowed.filter(c=>breadcrumb(c).toLowerCase().includes(categorySearch.trim().toLowerCase())).map(c=><button type="button" key={c.id} className={reviewCategoryIds.includes(Number(c.id))?"choice-button active":"choice-button"} onClick={()=>select(c)}>{breadcrumb(c)}</button>)}</div></section>;
                    const parent=categoryPath.at(-1),choices=allowed.filter(c=>String(c.parent_id||"")===String(parent?.id||""));
                    return <section><div className="category-breadcrumbs">{categoryPath.length>0&&<button type="button" className="text-button" onClick={()=>setCategoryPath(path=>path.slice(0,-1))}>← Back</button>}<span>{categoryPath.length?categoryPath.map(x=>x.custom_name).join(" › "):"Choose a main category"}</span></div><div className="review-choices">{choices.map(c=>{const hasChildren=allowed.some(x=>String(x.parent_id)===String(c.id));return <button type="button" key={c.id} className={reviewCategoryIds.includes(Number(c.id))?"choice-button active":"choice-button"} onClick={()=>hasChildren?setCategoryPath(path=>[...path,c]):select(c)}>{c.custom_name}{hasChildren?" ›":""}</button>})}</div></section>;
                  })()}
                </div>
                <button
                  className="primary modal-submit"
                  disabled={!reviewCategoryIds.length}
                  onClick={saveReviewCategories}
                >
                  Save categories and continue
                </button>
              </div>
            ) : (
              <div className="review-choices">
                {reviewChoices(review[reviewIndex]).map(([value, label]) => (
                  <button
                    className="choice-button"
                    key={String(value)}
                    onClick={() => reviewSave(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className="review-nav">
              <button
                className="secondary"
                title="Go to previous movement"
                disabled={reviewIndex === 0}
                onClick={() => {
                  const i = Math.max(0, reviewIndex - 1),
                    item = review[i];
                  setReviewIndex(i);
                  setReviewCategoryIds(
                    [
                      item.category_id,
                      ...(item.additional_categories || []).map((c) => c.id),
                    ]
                      .filter(Boolean)
                      .map(Number),
                  );
                }}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <button
                className="text-button"
                onClick={() => {
                  const item = review[reviewIndex];
                  setReview(null);
                  setEditing(item);
                  setModal(true);
                }}
              >
                <ExternalLink size={14} /> Open full edit
              </button>
              <button
                className="secondary"
                title="Go to next movement without changing it"
                disabled={reviewIndex >= review.length - 1}
                onClick={() => {
                  const i = Math.min(review.length - 1, reviewIndex + 1),
                    item = review[i];
                  setReviewIndex(i);
                  setReviewCategoryIds(
                    [
                      item.category_id,
                      ...(item.additional_categories || []).map((c) => c.id),
                    ]
                      .filter(Boolean)
                      .map(Number),
                  );
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
            <button
              className="primary modal-submit"
              onClick={() => setReview(null)}
            >
              Done / stop reviewing
            </button>
          </div>
        </Modal>
      )}
      {Array.isArray(review) && review.length === 0 && (
        <Modal
          title="Quick review complete"
          screenId="D0203"
          onClose={() => setReview(null)}
        >
          <div className="empty">
            <strong>Nothing else needs review</strong>
            <span>
              Change the scope to All movements if you want to review existing
              values.
            </span>
            <button
              className="primary modal-submit"
              onClick={() => setReview(null)}
            >
              Done
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
